import React, { useEffect, useMemo, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { MdAdd, MdSearch, MdUpload } from "react-icons/md";
import { partnersAPI } from "../services/api";
import { showToast } from "../services/toast";
import { validatePartner } from "../utils/partnerValidator";
import CustomPagination from "../components/comman/CustomPagination";
import CustomAction from "../components/comman/CustomAction";
import EditPartnerModal from "../components/modals/partners/EditPartnerModal";
import DeleteModal from "../components/modals/delete-modal/DeleteModal";
import ImportModal from "../components/modals/partners/ImportModal";
import { BIG_PAGE_OPTIONS } from "../utils/constants";
import SimpleTable from "../components/comman/custom-table/SimpleTable";

const initialForm = {
  partner_code: "",
  name: "",
  company_name: "",
  email: "",
  mobile_number: "",
  website: "",
  arn_afmi_no: "",
  // local-only helpers:
  logo: undefined,
  logoPreview: undefined,
};

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);

  const [form, setForm] = useState({
    partner_code: "",
    name: "",
    company_name: "",
    email: "",
    mobile_number: "",
    website: "",
    arn_afmi_no: "",
  });
  const [showImport, setShowImport] = useState(false);
  // const [currentPage, setCurrentPage] = useState(1);
  // const [pageSize, setPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [importReport, setImportReport] = useState(null); // Import report state
  const [importSuccess, setImportSuccess] = useState(null);

  const [fieldErrors, setFieldErrors] = useState({});
  // const fetchPartners = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await partnersAPI.getPartners();
  //     if (res.isSuccess) {
  //       setPartners(res.data.partners);
  //     }
  //   } catch (e) {
  //     toast.error("Failed to load partners");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  //   const startCreate = () => {
  //   setEditing(null);
  //   setForm({ partnerCode: "", name: "", email: "" }); // adjust fields if needed
  //   setShowModal(true);
  // };

  const fetchPartners = async (opts = {}) => {
    setLoading(true);
    try {
      const res = await partnersAPI.getPartners({
        page: opts.page ?? currentPage,
        limit: opts.limit ?? pageSize,
        search: opts.search ?? debouncedSearch,
      });

      if (res?.isSuccess) {
        const list = res.data?.partners ?? [];
        const pg = res.data?.pagination ?? {
          total: list.length,
          page: 1,
          limit: pageSize,
          totalPages: 1,
        };
        setPartners(list);
        setTotal(pg.total || 0);
        setTotalPages(pg.totalPages || 1);

        // If the API echoes back page/limit and they differ (e.g. server clamps values), sync them
        if (pg.page && pg.page !== currentPage) setCurrentPage(pg.page);
        if (pg.limit && pg.limit !== pageSize) setPageSize(pg.limit);
      } else {
        // toast.error(res?.message || "Failed to load partners");
        showToast("error", res?.message || "Failed to load partners");
      }
    } catch (e) {
      // toast.error("Failed to load partners");
      showToast("error", "Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchPartners();
  // }, []);

  useEffect(() => {
    fetchPartners({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearch]);

  const revokePreviewIfAny = (url) => {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {}
  };

  // Central reset for create/edit modal
  const resetPartnerForm = () => {
    // cleanup preview
    if (form?.logoPreview) revokePreviewIfAny(form.logoPreview);

    setForm(initialForm);
    setFieldErrors({});
    setEditing(null);
  };

  // Central close for create/edit modal (optional wrapper)
  const closeEditCreateModal = () => {
    setShowModal(false);
  };

  // Reset for delete modal
  const resetDeleteState = () => {
    setPartnerToDelete(null);
    setShowDeleteModal(false);
  };

  // Reset for import modal
  const resetImportState = () => {
    setImportReport(null);
    setImportSuccess(null);
    setShowImport(false);
  };

  const startCreate = () => {
    // setEditing(null);
    // setForm({
    //   partner_code: "",
    //   name: "",
    //   company_name: "",
    //   email: "",
    //   mobile_number: "",
    //   website: "",
    //   arn_afmi_no: "",
    // });
    resetPartnerForm();
    setShowModal(true);
  };

  const startEdit = (p) => {
    console.log("Editing partner:", p);

    if (form?.logoPreview) revokePreviewIfAny(form.logoPreview);

    setEditing(p);

    setForm({
      partner_code: p.partner_code || "",
      name: p.name || "",
      company_name: p.company_name || "",
      email: p.email || "",
      mobile_number: p.mobile_number || "",
      website: p.website || "",
      arn_afmi_no: p.arn_afmi_no || "",
      // file is undefined until user uploads new one
      logo: undefined,
      // 👇 API se aaya logo ka proper URL build karke set karo
      logoPreview: p.logo ? buildLogoUrl(p.logo) : undefined,
    });

    setFieldErrors({});
    setShowModal(true);
  };

  // const savePartner = async (e) => {
  //   e.preventDefault();
  //   try {
  //     if (editing) {
  //       await partnersAPI.updatePartner(editing.id, form);
  //       toast.success("Partner updated");
  //     } else {
  //       await partnersAPI.createPartner(form);
  //       toast.success("Partner created");
  //     }
  //     setShowModal(false);
  //     fetchPartners();
  //   } catch (e) {
  //     toast.error(e.response?.data?.message || "Save failed");
  //   }
  // };

  const savePartner = async (e) => {
    e.preventDefault();
    try {
      // 1) validate before building FormData
      const { valid, errors } = validatePartner(form, {
        mode: editing ? "update" : "add",
        existing: partners,
        currentId: editing?.id,
        requireEmail: false,
        requireMobile: false,
      });
      setFieldErrors(errors);
      if (!valid) {
        showToast("error", Object.values(errors)[0] || "Please fix the errors");
        return;
      }
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === "logo" || key === "logoPreview") return;
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      console.log("logo ", form?.logo);

      if (form.logo) {
        formData.append("file", form.logo);
      }

      if (editing) {
        await partnersAPI.updatePartner(editing.id, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("success", "Partner updated");
      } else {
        await partnersAPI.createPartner(formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("success", "Partner created");
      }

      setShowModal(false);
      setFieldErrors({});
      fetchPartners();
    } catch (e) {
      // toast.error(e.response?.data?.message || "Save failed");
      showToast("error", e.response?.data?.message || "Save failed");
    }
  };

  const removePartner = async () => {
    if (!partnerToDelete) return;
    try {
      await partnersAPI.deletePartner(partnerToDelete.id);
      // toast.success("Partner deleted");
      showToast("success", "Partner deleted");
      fetchPartners();
    } catch (e) {
      // toast.error("Delete failed");
      showToast("error", "Delete failed");
    } finally {
      setShowDeleteModal(false);
      setPartnerToDelete(null);
    }
  };

  // await partnersAPI.deletePartner(partnerToDelete.id);
  //   toast.success("Partner deleted");
  //   fetchPartners();
  // } catch (e) {
  //   toast.error("Delete failed");
  // } finally {
  //   setShowDeleteModal(false);
  //   setPartnerToDelete(null);
  // const onImport = async (e) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   try {
  //     const res = await partnersAPI.importPartners(file);
  //     if (res?.isSuccess) {
  //       // toast.success("Partners imported successfully");
  //       // // setShowImport(false);
  //       // setImportReport(res?.data); // Set the import report
  //       // setImportSuccess(true);
  //       // fetchPartners();

  //       if (res?.data?.errors && res.data.errors.length > 0) {
  //         setImportReport(res?.data); // Set the import error report
  //         setImportSuccess(false); // Mark as failed import
  //         setShowImport(true); // Show the import report modal
  //         // toast.error("Import failed due to validation errors");
  //         showToast("error", "Import failed due to validation errors");
  //       } else {
  //         // toast.success("Partners imported successfully");
  //         showToast("success", "Partners imported successfully");
  //         setImportReport(null); // Reset the report
  //         setImportSuccess(null); // Reset the import success state
  //         setShowImport(false); // Close the modal on successful import
  //         fetchPartners(); // Refresh the partner list
  //       }
  //     } else {
  //       console.log("dada");

  //       // toast.error(res.message || "Import failed!");
  //       showToast("error", res.message || "Import failed!");
  //       setImportReport(res?.data || null); // Set the error report
  //       setImportSuccess(false);
  //     }
  //   } catch (err) {
  //     console.log("vdvdvdvd", err?.response?.data?.data);

  //     // toast.error(err.response?.data?.message || "Import failed");
  //     showToast("error", err.response?.data?.message || "Import failed!");
  //     if (err?.response?.data?.data !== 0) {
  //       setImportReport(err.response?.data?.data); // Clear the report on error
  //       setImportSuccess(false);
  //     } else {
  //       setImportReport(null);
  //       setImportSuccess(null);
  //       setShowImport(false);
  //     }
  //   }
  // };
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportReport(null);
    setImportSuccess(null);

    try {
      const res = await partnersAPI.importPartners(file);

      if (res?.isSuccess) {
        if (res?.data?.errors && res.data.errors.length > 0) {
          setImportReport(res?.data);
          setImportSuccess(false);
          setShowImport(true);
          showToast("error", "Import failed due to validation errors");
        } else {
          showToast("success", "Partners imported successfully");
          setImportReport(null);
          setImportSuccess(null);
          setShowImport(false);
          fetchPartners();
        }
      } else {
        showToast("error", res.message || "Import failed!");
        setImportReport(res?.data || null);
        setImportSuccess(false);
        setShowImport(true);
      }
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Import failed!");
      const data = err?.response?.data?.data;
      if (data && data !== 0) {
        setImportReport(data);
        setImportSuccess(false);
        setShowImport(true);
      } else {
        setImportReport(null);
        setImportSuccess(null);
        setShowImport(false);
      }
    } finally {
      setImporting(false);
      e.target.value = ""; // reset input so same file can be chosen again
    }
  };

  // optional: compact page buttons when many pages
  // const pageItems = useMemo(() => {
  //   const items = [];
  //   const maxButtons = 7; // tune
  //   const add = (n) =>
  //     items.push(
  //       <Pagination.Item
  //         key={n}
  //         active={n === currentPage}
  //         onClick={() => setCurrentPage(n)}
  //       >
  //         {n}
  //       </Pagination.Item>
  //     );

  //   if (totalPages <= maxButtons) {
  //     for (let i = 1; i <= totalPages; i++) add(i);
  //     return items;
  //   }

  //   // windowing
  //   const windowSize = 3;
  //   const left = Math.max(2, currentPage - windowSize);
  //   const right = Math.min(totalPages - 1, currentPage + windowSize);

  //   add(1);
  //   if (left > 2) items.push(<Pagination.Ellipsis key="l-ell" disabled />);
  //   for (let i = left; i <= right; i++) add(i);
  //   if (right < totalPages - 1)
  //     items.push(<Pagination.Ellipsis key="r-ell" disabled />);
  //   add(totalPages);

  //   return items;
  // }, [currentPage, totalPages]);

  const downloadValidationErrorsCsv = (report) => {
    if (!report?.errors?.length) return;

    // Collect all data keys across error rows
    const dataKeys = Array.from(
      report.errors.reduce((set, r) => {
        Object.keys(r.data || {}).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );

    const header = ["row", "errors", ...dataKeys].join(",");
    const lines = report.errors.map((r) => {
      const errMsg = (r.errors || []).join("; ");
      const cells = dataKeys.map((k) => {
        let v = r.data?.[k];
        if (v === null || v === undefined) v = "";
        v = String(v).replace(/"/g, '""');
        return `"${v}"`;
      });
      return [`"${r.row}"`, `"${errMsg.replace(/"/g, '""')}"`, ...cells].join(
        ","
      );
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `order-import-validation-errors-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFieldError = (name) =>
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));

  const buildLogoUrl = (p = "") => {
    if (!p) return "";
    if (/^https?:\/\//i.test(p)) return p;
    const base = process.env.REACT_APP_API_URL || "";
    const clean = String(p).replace(/^\/+/, "");
    return base ? `${base}/${clean}` : `/${clean}`;
  };

  const partnerColumns = useMemo(
    () => [
      // Logo (thumb, not sortable)
      {
        id: "logo",
        header: "Logo",
        minWidth: 90,
        sortable: false,
        cell: (_v, row) =>
          row.logo ? (
            <img
              src={buildLogoUrl(row.logo)}
              alt={row.name || "Logo"}
              style={{
                width: 50,
                height: 50,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          ) : (
            <span className="text-muted">No Logo</span>
          ),
      },

      // Code
      {
        id: "partner_code",
        header: "Code",
        accessor: "partner_code",
        minWidth: 120,
        sortType: "text",
        cell: (v) => v || "—",
      },

      // Name
      {
        id: "name",
        header: "Name",
        accessor: "name",
        minWidth: 180,
        sortType: "text",
        cell: (v) => v || "—",
      },

      // Company
      {
        id: "company",
        header: "Company",
        accessor: "company_name",
        minWidth: 200,
        sortType: "text",
        cell: (v) => v || "—",
      },

      // Email
      {
        id: "email",
        header: "Email",
        accessor: "email",
        minWidth: 220,
        sortType: "text",
        cell: (v) =>
          v ? (
            <a href={`mailto:${v}`} className="text-decoration-none">
              {v}
            </a>
          ) : (
            "—"
          ),
      },

      // Mobile
      {
        id: "mobile",
        header: "Mobile",
        accessor: "mobile_number",
        minWidth: 150,
        // Sort by digits only so +91, spaces, dashes don’t break sorting
        sortType: (a, b) => {
          const norm = (x) => String(x ?? "").replace(/\D+/g, "");
          const da = norm(a?.mobile_number);
          const db = norm(b?.mobile_number);
          if (!da && !db) return 0;
          if (!da) return -1;
          if (!db) return 1;
          if (da < db) return -1;
          if (da > db) return 1;
          return 0;
        },
        cell: (v) => v || "—",
      },

      // Website
      {
        id: "website",
        header: "Website",
        accessor: "website",
        minWidth: 180,
        sortType: "text",
        cell: (v) =>
          v ? (
            <a
              href={/^https?:\/\//i.test(v) ? v : `https://${v}`}
              target="_blank"
              rel="noreferrer"
              className="text-decoration-none"
            >
              {v}
            </a>
          ) : (
            "—"
          ),
      },

      // ARN/AFMI
      {
        id: "arn",
        header: "ARN/AFMI",
        accessor: "arn_afmi_no",
        minWidth: 140,
        sortType: "text",
        cell: (v) => v || "—",
      },
      {
        id: "creator.name",
        header: "Created By",
        accessor: "creator", // directly use the creator object
        minWidth: 220,
        cell: (creator) => {
          if (!creator) return "—";

          const name = creator?.name || "—";
          const email = creator?.email || "—";
          const mobile = creator?.mobileNumber || "—";

          return (
            <div className="d-flex flex-column lh-sm gap-1">
              <div>
                <strong>{name}</strong>
              </div>
              <div>
                <strong>{email}</strong>
              </div>
              <div>
                <strong>{mobile}</strong>
              </div>
            </div>
          );
        },
      },
      {
        id: "created_at",
        header: "Created At",
        accessor: "created_at", // change to "createdAt" if your API uses camelCase
        minWidth: 160,
        sortType: "date",
        cell: (v, row) => {
          // fallback if API returns createdAt instead of created_at
          const raw = v ?? row?.created_at;
          if (!raw) return "—";
          const dt = new Date(raw);
          if (isNaN(+dt)) return "—";

          const dateStr = dt.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const timeStr = dt.toLocaleString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <div className="d-flex flex-column lh-sm gap-1">
              <strong>{dateStr}</strong>
              <p className="m-0">{timeStr}</p>
            </div>
          );
        },
      },

      // Actions
      {
        id: "actions",
        header: "Actions",
        isActions: true, // SimpleTable knows to render Edit/Delete buttons
        minWidth: 140,
        className: "text-nowrap",
      },
      // {
      //   id: "actions",
      //   header: "Actions",
      //   minWidth: 130,
      //   sortable: false,
      //   cell: (_v, row) => (
      //     <div className="d-flex align-items-center">
      //       <Button
      //         size="sm"
      //         className="table-btn me-2"
      //         title="Edit"
      //         onClick={() => startEdit(row)}
      //       >
      //         <MdEdit />
      //       </Button>
      //       <Button
      //         size="sm"
      //         className="table-btn"
      //         title="Delete"
      //         onClick={() => {
      //           setPartnerToDelete(row);
      //           setShowDeleteModal(true);
      //         }}
      //       >
      //         <MdDelete />
      //       </Button>
      //     </div>
      //   ),
      // },
    ],
    []
  );
  return (
    <>
      <div className="h-100 overflow-auto position-relative z-1">
        {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Partners Management</h4>
        <div>
          <Button
            variant="primary"
            className="me-2"
            onClick={() => setShowImport(true)}
          >
            <MdUpload className="me-1" /> Import Excel
          </Button>
          <Button variant="primary" onClick={startCreate}>
            <MdAdd className="me-1" /> Add Partner
          </Button>
        </div>
      </div> */}
        <div className="d-flex justify-content-between align-items-center mb-1 p-3 sticky-top bg-body">
          <h4 className="mb-0">Partners</h4>
          <div className="d-flex flex-end gap-2">
            <InputGroup className="search-bar border-primary rounded-2">
              <Form.Control
                placeholder="Search partners..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <InputGroup.Text>
                <MdSearch />
              </InputGroup.Text>
            </InputGroup>
            <CustomAction
              actions={[
                {
                  label: "Import Excel",
                  icon: MdUpload,
                  onClick: () => setShowImport(true),
                },
                {
                  label: "Add Partner",
                  icon: MdAdd,
                  onClick: startCreate,
                },
              ]}
            />
          </div>
        </div>
        <div className="content-cover px-3 position-relative">
          {/* <div className="table-cover">
            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <Table hover bordered className="position-relative z-1 mb-0">
                  <thead className="sticky-top">
                    <tr>
                      <th>Logo</th>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Website</th>
                      <th>ARN/AFMI</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.logo ? (
                            <img
                              src={`${process.env.REACT_APP_API_URL}/${p.logo}`}
                              alt={p.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                          ) : (
                            <span className="text-muted">No Logo</span>
                          )}
                        </td>
                        <td>{p.partner_code}</td>
                        <td>{p.name}</td>
                        <td>{p.company_name || "-"}</td>
                        <td>{p.email || "-"}</td>
                        <td>{p.mobile_number || "-"}</td>
                        <td>{p.website || "-"}</td>
                        <td>{p.arn_afmi_no || "-"}</td>
                        <td>
                          
                          <Button
                            size="sm"
                            // variant="outline-primary"
                            className="table-btn me-2 "
                            onClick={() => startEdit(p)}
                            title="Edit"
                          >
                            <MdEdit />
                          </Button>
                          
                          
                          <Button
                            size="sm"
                            // variant="outline-danger"
                            className="table-btn "
                            onClick={() => {
                              setPartnerToDelete(p);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <MdDelete />
                          </Button>
                          
                        </td>
                      </tr>
                    ))}
                    {partners.length === 0 && !loading && (
                      <tr>
                        <td colSpan={8} className="text-center text-muted">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </div> */}

          <SimpleTable
            loading={loading}
            data={partners}
            columns={partnerColumns}
            keyField="id"
            responsive={true}
            emptyMessage="No records found"
            onEdit={(row) => startEdit(row)}
            onDelete={(row) => {
              setPartnerToDelete(row);
              setShowDeleteModal(true);
            }}
          />

          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            onPageChange={setCurrentPage}
            className={"sticky-bottom"}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            total={total}
            perPageOptions={BIG_PAGE_OPTIONS}
          />
        </div>
      </div>
      {/* Create/Edit Modal */}
      <EditPartnerModal
        show={showModal}
        onHide={closeEditCreateModal}
        onExited={resetPartnerForm}
        isEdit={editing}
        clearFieldError={clearFieldError}
        fieldErrors={fieldErrors}
        form={form}
        setForm={setForm}
        savePartner={savePartner}
        onClose={() => setShowModal(false)}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onHide={resetDeleteState}
        onExited={resetDeleteState}
        title={"Delete Partner"}
        message={`Are you sure you want to delete <strong>${partnerToDelete?.name}</strong>?`}
        confirmDelete={removePartner}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Import Modal */}
      <ImportModal
        show={showImport}
        importing={importing}
        onHide={resetImportState}
        onExit={resetImportState}
        importSuccess={importSuccess}
        importReport={importReport}
        onImport={onImport}
        downloadValidationErrorsCsv={downloadValidationErrorsCsv}
      />

      {/* <Modal
        show={showImport}
        // onHide={() => {
        //   setImportReport(null);
        //   setImportSuccess(null);
        //   setShowImport(false);
        // }}
        onHide={resetImportState}
        onExited={resetImportState}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Partners from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importSuccess === null && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Select .xlsx/.xls file</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onImport}
                />
              </Form.Group>
              <div className="text-muted small">
                Ensure your file matches the required format.
              </div>
            </div>
          )}

          {importSuccess === true && importReport && (
            <div>
              <Row className="g-3 mb-3">
                <Col sm={6} md={3}>
                  <Badge bg="secondary" className="w-100 p-2">
                    Total: {importReport.totalRows ?? 0}
                  </Badge>
                </Col>
                <Col sm={6} md={3}>
                  <Badge bg="success" className="w-100 p-2">
                    Created: {importReport.createdCount ?? 0}
                  </Badge>
                </Col>
                <Col sm={6} md={3}>
                  <Badge bg="info" className="w-100 p-2">
                    Updated: {importReport.updatedCount ?? 0}
                  </Badge>
                </Col>
                <Col sm={6} md={3}>
                  <Badge bg="danger" className="w-100 p-2">
                    Errors: {importReport.errorCount ?? 0}
                  </Badge>
                </Col>
              </Row>
              
              
            </div>
          )}

          {importSuccess === false && importReport && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Validation Errors</h6>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => downloadValidationErrorsCsv(importReport)}
                >
                  Download errors (.csv)
                </Button>
              </div>

        
              <Table bordered responsive size="sm">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Row</th>
                    <th>Errors</th>
                    <th>Data (original)</th>
                  </tr>
                </thead>
                <tbody>
                  {importReport.errors?.map((error, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{error.row}</td>
                      <td>
                        <ul className="mb-0 ps-3">
                          {error.errors?.map((msg, idx) => (
                            <li key={idx}>{msg}</li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <code className="small">
                          {JSON.stringify(error.data, null, 0)}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {importSuccess === null ? (
            <Button
              variant="secondary"
              onClick={() => {
                setImportReport(null);
                setImportSuccess(null);
                setShowImport(false);
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setImportReport(null);
                  setImportSuccess(null);
                  setShowImport(false);
                }}
              >
                Done
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal> */}

      {/* <Modal
        show={showDeleteModal}
        // onHide={() => setShowDeleteModal(false)}
        onHide={resetDeleteState}
        onExited={resetDeleteState}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{partnerToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={removePartner}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal> */}
      {/* <Modal
        show={showModal}
        // onHide={() => setShowModal(false)}
        onHide={closeEditCreateModal}
        onExited={resetPartnerForm}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Partner" : "Add Partner"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={savePartner}>
          <Modal.Body>
            <Row className="g-3">
  <Col xs={12}>
                <Form.Group className="d-grid columns-2 gap-3" style={{height: "80px"}}>
                  <div >
                  <Form.Label>Logo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Preview + save file in form
                        const url = URL.createObjectURL(file);
                        setForm({ ...form, logo: file, logoPreview: url });
                        clearFieldError("logo");
                      }
                    }}
                  />
                  </div>
                  {form.logoPreview && (
                    <figure className="d-flex align-items-center justify-content-center m-0 h-100" >
                    <Image
                      src={form.logoPreview}
                      alt="Preview"
                     style={{height: "80px"}}
                      fluid
                      className="rounded-2 "
                    />
                    </figure>
                  )}
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Partner Code*</Form.Label>
                  <Form.Control
                    // required
                    value={form.partner_code}
                    // onChange={(e) =>
                    //   setForm({ ...form, partner_code: e.target.value })
                    // }
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase(); // keep uppercase
                      setForm({ ...form, partner_code: v });
                      clearFieldError("partner_code");
                    }}
                    disabled={!!editing}
                    isInvalid={!!fieldErrors.partner_code}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.partner_code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

            
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Name*</Form.Label>
                  <Form.Control
                    // required
                    value={form.name}
                    // onChange={(e) => setForm({ ...form, name: e.target.value })}
                    onChange={(e) => {
                      setForm({ ...form, name: e.target.value });
                      clearFieldError("name");
                    }}
                    isInvalid={!!fieldErrors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    value={form.company_name}
                    onChange={(e) =>
                      setForm({ ...form, company_name: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email*</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    // onChange={(e) =>
                    //   setForm({ ...form, email: e.target.value })
                    // }
                    onChange={(e) => {
                      setForm({ ...form, email: e.target.value.trim() });
                      clearFieldError("email");
                    }}
                    isInvalid={!!fieldErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Mobile Number*</Form.Label>
                  <Form.Control
                    value={form.mobile_number}
                    // onChange={(e) =>
                    //   setForm({ ...form, mobile_number: e.target.value })
                    // }
                    onChange={(e) => {
                      setForm({ ...form, mobile_number: e.target.value });
                      clearFieldError("mobile_number");
                    }}
                    isInvalid={!!fieldErrors.mobile_number}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.mobile_number}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    value={form.website}
                    onChange={(e) =>
                      setForm({ ...form, website: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>ARN/AFMI No</Form.Label>
                  <Form.Control
                    value={form.arn_afmi_no}
                    onChange={(e) =>
                      setForm({ ...form, arn_afmi_no: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editing ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal> */}
    </>
  );
};
export default Partners;
